'use client'

import { useMemo } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { categoryAccent } from '@/lib/category-accent'

export interface HeaderTopCategory {
  id: number
  title: string
  slug: string
  color?: string | null
}

const HEADER_NAV_LIMIT = 14

type Props = {
  categories: HeaderTopCategory[]
  /** Ölçüm için görünmez kopya */
  measure?: boolean
  className?: string
}

export default function HeaderTopCategories({
  categories,
  measure = false,
  className,
}: Props) {
  const pathname = usePathname()

  const items = useMemo(
    () =>
      categories
        .slice(0, HEADER_NAV_LIMIT)
        .filter((c) => c.slug && c.title),
    [categories],
  )

  const activeSlug = useMemo(() => {
    const seg = pathname.split('/').filter(Boolean)[0]
    return seg || null
  }, [pathname])

  if (items.length === 0) return null

  return (
    <nav
      className={cn(
        'flex min-w-0 items-center gap-0.5 sm:gap-1 md:gap-1.5',
        measure && 'w-max',
        className,
      )}
      aria-label={measure ? undefined : 'Üst menü kategorileri'}
      aria-hidden={measure || undefined}
    >
      {items.map((cat) => {
        const active = activeSlug === cat.slug
        const accent = categoryAccent(cat.color)
        return (
          <Link
            key={cat.id}
            href={`/${cat.slug}`}
            tabIndex={measure ? -1 : undefined}
            className={cn(
              'flex h-9 shrink-0 items-center whitespace-nowrap rounded-sm px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide outline-none sm:h-10 sm:px-2 sm:text-xs md:text-sm',
              'transition-colors duration-150',
              !measure &&
                'focus-visible:ring-2 focus-visible:ring-[#D92E2E] focus-visible:ring-offset-1 dark:focus-visible:ring-offset-slate-950',
              active
                ? ''
                : 'text-gray-700 dark:text-gray-300',
              !active && !measure && 'hover:opacity-90',
            )}
            style={measure ? undefined : { ['--cat-accent' as string]: accent }}
            aria-current={!measure && active ? 'page' : undefined}
          >
            <span
              className={cn(active && !measure && 'underline decoration-2 underline-offset-[4px]')}
              style={active && !measure ? { textDecorationColor: accent } : undefined}
            >
              {cat.title}
            </span>
          </Link>
        )
      })}
    </nav>
  )
}
